/**
 * @module useAI
 * @description React Query hooks for AI service operations
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Hook to get AI suggestions
 * @returns {Object} Query result with suggestions data
 */
export const useAISuggestions = () => {
  return useQuery({
    queryKey: ['ai', 'suggestions'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/suggestions`);
      return data;
    },
  });
};

/**
 * Hook to get AI capabilities
 * @returns {Object} Query result with capabilities data
 */
export const useAICapabilities = () => {
  return useQuery({
    queryKey: ['ai', 'capabilities'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/capabilities`);
      return data;
    },
  });
};

/**
 * Hook to get AI models
 * @returns {Object} Query result with models data
 */
export const useAIModels = () => {
  return useQuery({
    queryKey: ['ai', 'models'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/models`);
      return data;
    },
  });
};

/**
 * Hook to send chat message to AI
 * @returns {Object} Mutation object for sending chat messages
 */
export const useSendAIChat = () => {
  return useMutation({
    mutationFn: async (messageData) => {
      const { data } = await api.post(`${API_ENDPOINTS.AI}/chat`, messageData);
      return data;
    },
  });
};

/**
 * Hook to generate image with AI
 * @returns {Object} Mutation object for generating images
 */
export const useGenerateAIImage = () => {
  return useMutation({
    mutationFn: async (imageData) => {
      const { data } = await api.post(`${API_ENDPOINTS.AI}/generate-image`, imageData);
      return data;
    },
  });
};

/**
 * Hook to transcribe audio with AI
 * @returns {Object} Mutation object for transcribing audio
 */
export const useTranscribeAudio = () => {
  return useMutation({
    mutationFn: async (formData) => {
      const { data } = await api.post(`${API_ENDPOINTS.AI}/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return data;
    },
  });
};

/**
 * Hook to get AI chat messages
 * @returns {Object} Query result with chat messages data
 */
export const useAIChatMessages = () => {
  return useQuery({
    queryKey: ['ai', 'chat-messages'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/chat-messages`);
      return data;
    },
  });
};

/**
 * Hook to get AI anomalies
 * @returns {Object} Query result with anomalies data
 */
export const useAIAnomalies = () => {
  return useQuery({
    queryKey: ['ai', 'anomalies'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/anomalies`);
      return data;
    },
  });
};

/**
 * Hook to get AI predictions
 * @returns {Object} Query result with predictions data
 */
export const useAIPredictions = () => {
  return useQuery({
    queryKey: ['ai', 'predictions'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/predictions`);
      return data;
    },
  });
};

/**
 * Hook to get AI agents
 * @returns {Object} Query result with agents data
 */
export const useAIAgents = () => {
  return useQuery({
    queryKey: ['ai', 'agents'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/agents`);
      return data;
    },
  });
};

/**
 * Hook to get AI statistics (tokens used, usage cycle, trends)
 * @returns {Object} Query result with AI stats data
 */
export const useAIStats = () => {
  return useQuery({
    queryKey: ['ai', 'stats'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AI}/stats`);
      return data;
    },
  });
};
